import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  FieldValues,
  useForm,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { GrFormClose } from 'react-icons/gr';
import api from '~/services/api';
import { tabelaCodigoSaida } from '~/utils/tabelaSped/tabelaCodigoSaida';
import { tabelaNaturezaReceita } from '~/utils/tabelaSped/tabelaNaturezaReceita';

import { CustomButtonNovo } from '../Buttons/CustomButtonNovo';
import { InputSelect } from './InputSelect';
import { BootstrapModal } from './styles';
import { schemModalSped } from './validations';

interface TabelaCSTItem {
  value: string;
  label: string;
}

interface SpedOption {
  value: string;
  label: string;
  descricao: string;
}

interface ModalSpedProps {
  enabledModalSped: boolean;
  handleChangeEnabledModal: () => void;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  clearErrors?: UseFormClearErrors<FieldValues>;
  item?: number | string;
  onChange?: () => void;
}

const buildField = (base: string, item?: number | string): string =>
  item === undefined || item === null ? base : `${base}_${item}`;

const formatSpedOptions = (opcoes: any[]): SpedOption[] =>
  opcoes.map((option) => ({
    value: option.cod_tab_sped.toString().padStart(3, '0'),
    descricao: option.des_tab_sped,
    label: option.descricao,
  }));

const ModalSped: React.FC<ModalSpedProps> = ({
  enabledModalSped,
  handleChangeEnabledModal,
  register,
  setValue,
  getValues,
  clearErrors,
  item,
  onChange,
}) => {
  const {
    register: modalRegister,
    control: modalControl,
    setValue: setValueModal,
    reset: resetModal,
    getValues: getValuesModal,
    clearErrors: clearErrorsModal,
    handleSubmit: handleSubmitModal,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onBlur',
    resolver: yupResolver(schemModalSped),
  });

  const [selectTabela, setSelectTabela] = useState<TabelaCSTItem[]>([]);
  const [codigosPorTabela, setCodigosPorTabela] = useState<SpedOption[]>([]);
  const [clearFields, setClearFields] = useState(false);

  const isGlobalForm = item === undefined || item === null;

  const cstSaidaField = buildField('cst_saida', item);
  const tipoNaoPisCofinsField = buildField('tipo_nao_pis_cofins', item);
  const desSpedField = buildField('des_sped', item);
  const codSpedField = buildField('cod_sped', item);
  const codTabelaField = buildField('cod_tabela', item);

  const tabelaCSTArray = useMemo<TabelaCSTItem[]>(
    () =>
      Object.keys(tabelaNaturezaReceita).map((key) => ({
        value: key,
        label: `Tabela ${key} - ${tabelaNaturezaReceita[key]}`,
      })),
    [],
  );

  const fetchSpedCodigos = useCallback(async (tabela: string) => {
    try {
      const { data } = await api.post('/produto/consulta-sped', {
        cod_tabela: tabela && tabela.toString(),
      });
      setCodigosPorTabela(formatSpedOptions(data.data));
    } catch {
      setCodigosPorTabela([]);
    }
  }, []);

  useEffect(() => {
    setSelectTabela([]);

    const codTabela = getValues(cstSaidaField);
    if (codTabela?.value === undefined) return;

    const formataCodTabela: string = codTabela.value
      .toString()
      .padStart(2, '0');
    const codigoTabela = tabelaCodigoSaida[formataCodTabela];

    const tabela = tabelaCSTArray.filter((entry) =>
      Array.isArray(codigoTabela)
        ? codigoTabela.includes(entry.value)
        : entry.value === codigoTabela,
    );

    setSelectTabela(tabela);

    if (tabela.length === 1) {
      setValueModal('des_tab_sped', tabela[0]);
      fetchSpedCodigos(tabela[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledModalSped, item]);

  useEffect(() => {
    setValueModal('des_tab_sped', null);
    setValueModal('cod_tab_sped', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues(tipoNaoPisCofinsField)]);

  const closeModal = useCallback(() => {
    setCodigosPorTabela([]);
    setSelectTabela([]);
    setClearFields(true);
    resetModal();

    setTimeout(() => {
      setValueModal('des_tab_sped', null);
      setValueModal('cod_tab_sped', null);
      setClearFields(false);
    }, 100);

    clearErrorsModal();
    handleChangeEnabledModal();
  }, [clearErrorsModal, handleChangeEnabledModal, resetModal, setValueModal]);

  const handleClear = useCallback(() => {
    setCodigosPorTabela([]);
    setSelectTabela([]);
    setValue(desSpedField, null);
    setValue(codSpedField, null);
    if (isGlobalForm) setValue(codTabelaField, null);
    closeModal();
  }, [
    closeModal,
    codSpedField,
    codTabelaField,
    desSpedField,
    isGlobalForm,
    setValue,
  ]);

  const handleConfirmAction = handleSubmitModal((data) => {
    const { cod_tab_sped, des_tab_sped } = data;

    setValue(desSpedField, !cod_tab_sped ? null : cod_tab_sped.descricao);
    setValue(
      codSpedField,
      !cod_tab_sped ? null : cod_tab_sped.value.toString().padStart(3, '0'),
    );

    if (isGlobalForm) {
      setValue(codTabelaField, des_tab_sped.value);
      clearErrors?.(desSpedField);
      clearErrors?.(codSpedField);
    }

    closeModal();
  });

  const handleTabelaChange = async (newValue: TabelaCSTItem) => {
    clearErrorsModal('des_tab_sped');
    clearErrorsModal('cod_tab_sped');

    const oldValue = getValuesModal('des_tab_sped');
    if (newValue.value === oldValue?.value) return;

    setValueModal('des_tab_sped', newValue);
    setValueModal('cod_tab_sped', null);
    onChange?.();
    await fetchSpedCodigos(newValue.value);
  };

  const handleCodigoChange = (newValue: SpedOption) => {
    clearErrorsModal('cod_tab_sped');
    setValueModal('cod_tab_sped', newValue);
    onChange?.();
  };

  return (
    <Modal show={enabledModalSped} onHide={closeModal} centered size="lg">
      <BootstrapModal.Header>
        <BootstrapModal.Title>Tabela SPED</BootstrapModal.Title>
        <GrFormClose
          size={20}
          style={{ cursor: 'pointer' }}
          onClick={closeModal}
        />
      </BootstrapModal.Header>
      <BootstrapModal.Body>
        <div className="row p-2">
          <div className="col-12 col-md-12">
            <InputSelect
              label="Tabela"
              name="des_tab_sped"
              placeholder="Selecione..."
              register={modalRegister}
              control={modalControl}
              options={selectTabela}
              clearCampo={clearFields}
              changeSelected={handleTabelaChange}
              isError={!!errors.des_tab_sped}
            />
          </div>

          <div className="col-12 col-md-12">
            <InputSelect
              label="Código"
              name="cod_tab_sped"
              placeholder="Selecione..."
              control={modalControl}
              register={register}
              options={codigosPorTabela || []}
              clearCampo={clearFields}
              changeSelected={handleCodigoChange}
              isError={!!errors.cod_tab_sped}
            />
          </div>
        </div>
      </BootstrapModal.Body>
      <div className="d-flex justify-content-center aliggn-center w-full mt-2 mb-4 gap-4">
        <CustomButtonNovo
          onClick={handleConfirmAction}
          label="Confirmar"
          variant="confirm"
        />
        <CustomButtonNovo
          onClick={handleClear}
          label="Limpar"
          variant="clear"
        />
      </div>
      <BootstrapModal.Footer />
    </Modal>
  );
};

export default ModalSped;
